import { useState } from "react";
import { X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableWrapper } from "@/components/ui/table";
import { StaleIndicator } from "@/components/ui/status";
import { StaleState } from "@/components/ui/system-state";
import { SpecimenCard, SpecimenSection } from "@/features/system-states/system-state-page";

const SOURCES: { source: string; records: string; last: string; age: string; stale: boolean }[] = [
  { source: "VaultCore (custody)", records: "14 balances", last: "08:31:04 UTC", age: "8m ago", stale: true },
  { source: "Fireblocks (signing)", records: "3 pending", last: "08:33:12 UTC", age: "6m ago", stale: true },
  { source: "Chainalysis (screening)", records: "22 checks", last: "08:39:41 UTC", age: "just now", stale: false },
  { source: "Banking (fiat rails)", records: "2 accounts", last: "08:38:50 UTC", age: "1m ago", stale: false },
];

export default function SysStale() {
  const [dismissed, setDismissed] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [synced, setSynced] = useState(false);

  function refresh() {
    setSyncing(true);
    setTimeout(() => {
      setSyncing(false);
      setSynced(true);
    }, 1200);
  }

  return (
    <>
      <SpecimenSection label="Stale source banner">
        {dismissed ? (
          <SpecimenCard className="max-w-[640px]">
            <p className="text-sm text-muted">
              Banner dismissed. The degraded freshness remains visible inline on each affected row below.
            </p>
            <Button variant="secondary" className="mt-3" onClick={() => setDismissed(false)}>
              Restore banner
            </Button>
          </SpecimenCard>
        ) : (
          <StaleState
            className="max-w-[900px]"
            title="Balance data may be stale"
            description="VaultCore has not refreshed within its 5 minute SLA. Balances and pending operations continue to use last confirmed values."
            lastSyncedAt={synced ? "Last synced just now" : "Last synced 8m ago"}
            action={
              <div className="flex items-center gap-2">
                <Button size="sm" variant="secondary" loading={syncing} onClick={refresh}>
                  Refresh now
                </Button>
                <button
                  type="button"
                  aria-label="Dismiss stale banner"
                  onClick={() => setDismissed(true)}
                  className="grid size-7 place-items-center rounded-sm text-warning hover:bg-warning-soft"
                >
                  <X aria-hidden className="size-4" />
                </button>
              </div>
            }
          />
        )}
      </SpecimenSection>

      <SpecimenSection label="Affected data — freshness per source">
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <th>Data source</th>
                <th>Records</th>
                <th>Last updated</th>
                <th>Data age</th>
                <th>Freshness</th>
              </tr>
            </thead>
            <tbody>
              {SOURCES.map((row) => {
                const stale = synced ? false : row.stale;
                return (
                  <tr key={row.source}>
                    <td className="font-semibold text-ink">{row.source}</td>
                    <td className="text-muted">{row.records}</td>
                    <td className="font-mono text-xs text-muted">{synced ? "09:04:00 UTC" : row.last}</td>
                    <td className="text-muted">{synced ? "just now" : row.age}</td>
                    <td>
                      {stale ? (
                        <Badge variant="warning" dot={false}>
                          Stale
                        </Badge>
                      ) : (
                        <StaleIndicator stale={false} label="Fresh" />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </TableWrapper>
      </SpecimenSection>

      <SpecimenSection label="Behaviour">
        <SpecimenCard label="Recovery">
          <ul className="flex list-disc flex-col gap-1.5 pl-5 text-sm text-muted">
            <li>Records remain readable and auditable using last confirmed values.</li>
            <li>The stale banner clears automatically once the provider refresh succeeds.</li>
            <li>Reads are unaffected; entries that depend on live balance are annotated, never silently applied.</li>
          </ul>
        </SpecimenCard>
      </SpecimenSection>
    </>
  );
}
